import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Tabs, Tab, Grid } from "@mui/material";
import FormDrawer from "../../components/layout/FormDrawer";
import { BRAND, primaryButtonSx } from "../../components/auth/authTheme";
import { IMAGE_URL } from "../../services/api";
import ProtectedImage from "../../components/ProtectedImage";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import StarOutlineOutlinedIcon from "@mui/icons-material/StarOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

const DetailItem = ({ icon, label, value }) => (
  <Grid item xs={12} sm={6}>
    <Box
      sx={{
        p: 2,
        borderRadius: "12px",
        bgcolor: "#fafafa",
        border: `1px solid ${BRAND.border}`,
        height: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
      }}
    >
      {icon && <Box sx={{ color: BRAND.orange, display: "flex", mt: 0.5 }}>{icon}</Box>}
      <Box sx={{ width: "100%", overflow: "hidden" }}>
        <Typography variant="caption" sx={{ color: BRAND.muted, display: "block", mb: 0.5, fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: BRAND.navy, fontWeight: 600, wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

const AppraisalViewModal = ({ show, handleClose, selectedCycle }) => {
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (show) {
      setTabValue(0);
    }
  }, [show]);

  const handleTabChange = (_, newValue) => setTabValue(newValue);

  const status = selectedCycle?.status || "pending";

  const renderProfileHeader = () => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 3, p: 2, bgcolor: "#fafafa", borderRadius: "12px", border: `1px solid ${BRAND.border}` }}>
      {selectedCycle?.profile_image ? (
        <Box
          sx={{
            width: 64,
            height: 64,
            minWidth: 64,
            minHeight: 64,
            flexShrink: 0,
            borderRadius: "50%",
            overflow: "hidden",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: BRAND.orange,
            color: "#fff",
            fontWeight: "bold",
            fontSize: 24,
            textTransform: "uppercase",
          }}
        >
          {selectedCycle?.employee_name?.split(" ")[0]?.[0]}
          {selectedCycle?.employee_name?.split(" ")[1]?.[0]}
          <ProtectedImage
            path={
              selectedCycle.profile_image.startsWith("data:")
                ? selectedCycle.profile_image
                : `${IMAGE_URL}${selectedCycle.profile_image}`
            }
            alt="Profile"
            style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", top: 0, left: 0 }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: 64,
            height: 64,
            minWidth: 64,
            minHeight: 64,
            flexShrink: 0,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: BRAND.orange,
            color: "#fff",
            fontWeight: "bold",
            fontSize: 24,
            textTransform: "uppercase",
          }}
        >
          {selectedCycle?.employee_name?.split(" ")[0]?.[0]}
          {selectedCycle?.employee_name?.split(" ")[1]?.[0]}
        </Box>
      )}
      <Box sx={{ ml: 2, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: BRAND.navy }}>
            {selectedCycle?.employee_name}
          </Typography>
          <Typography variant="caption" sx={{ color: BRAND.muted }}>
            ({selectedCycle?.employee_id || "N/A"})
          </Typography>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: selectedCycle?.is_active ? "#00cc00" : "red",
              ml: 1,
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: BRAND.muted, mt: 0.5 }}>
          {selectedCycle?.designation || "N/A"}
        </Typography>
      </Box>
      <Box>
        <span className={`leave-status-badge ${status}`}>{status}</span>
      </Box>
    </Box>
  );

  return (
    <FormDrawer
      open={show}
      onClose={handleClose}
      title="Appraisal Details"
      subtitle="Review appraisal cycle and reviewer feedback"
      width={{ xs: "100%", sm: 600, md: 820 }}
      footer={
        <Button onClick={handleClose} variant="contained" color="primary" sx={{ ...primaryButtonSx, minWidth: 120 }}>
          Close
        </Button>
      }
    >
      {selectedCycle ? (
        <Box>
          {renderProfileHeader()}

          <Box sx={{ borderBottom: 1, borderColor: BRAND.border, mb: 3 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  color: BRAND.muted,
                  "&.Mui-selected": { color: BRAND.orange },
                },
                "& .MuiTabs-indicator": { backgroundColor: BRAND.orange },
              }}
            >
              <Tab label="Appraisal Details" />
              <Tab label="Reviewers Details" />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <Grid container spacing={2}>
              <DetailItem icon={<CalendarTodayOutlinedIcon fontSize="small" />} label="Cycle Start" value={selectedCycle?.cycle_start} />
              <DetailItem icon={<CalendarTodayOutlinedIcon fontSize="small" />} label="Cycle End" value={selectedCycle?.cycle_end} />
              <DetailItem icon={<AttachMoneyOutlinedIcon fontSize="small" />} label="Old Salary" value={`₹${selectedCycle?.old_salary || 0}`} />
              <DetailItem icon={<TrendingUpOutlinedIcon fontSize="small" />} label="Hike (%)" value={`${selectedCycle?.hike_percentage || 0}%`} />
              <DetailItem icon={<AttachMoneyOutlinedIcon fontSize="small" />} label="New Salary" value={`₹${selectedCycle?.new_salary || 0}`} />
              <DetailItem icon={<CalendarTodayOutlinedIcon fontSize="small" />} label="Effective From" value={selectedCycle?.effective_from} />
              <DetailItem icon={<StarOutlineOutlinedIcon fontSize="small" />} label="Average Rating" value={selectedCycle?.average_rating} />
              <DetailItem icon={<InfoOutlinedIcon fontSize="small" />} label="Status" value={selectedCycle?.status} />
            </Grid>
          )}

          {tabValue === 1 && (
            <Box>
              {selectedCycle?.reviewers_details?.length ? (
                <Grid container spacing={2}>
                  {selectedCycle.reviewers_details.map((rev, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: "flex", flexDirection: "column", border: `1px solid ${BRAND.border}`, borderRadius: "12px", p: 2, bgcolor: "#fafafa", height: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                          {rev.profile_image ? (
                            <ProtectedImage
                              path={rev.profile_image.startsWith("data:") ? rev.profile_image : `${IMAGE_URL}${rev.profile_image}`}
                              alt={rev.employee_name}
                              style={{ width: "48px", height: "48px", borderRadius: "50%", objectFit: "cover" }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: BRAND.orange,
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: 16,
                                textTransform: "uppercase",
                              }}
                            >
                              {rev.employee_name?.split(" ")[0]?.[0]}
                              {rev.employee_name?.split(" ")[1]?.[0]}
                            </Box>
                          )}
                          <Box sx={{ ml: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" fontWeight="600" sx={{ color: BRAND.navy }}>
                                {rev.employee_name}
                              </Typography>
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: rev.is_active ? "#00cc00" : "red" }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: BRAND.muted, display: "block" }}>
                              {rev.designation || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ flex: 1, bgcolor: "#fff", borderRadius: "8px", p: 1.5, border: `1px solid ${BRAND.border}` }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                            <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 500 }}>
                              Rating: <span style={{ color: BRAND.navy, fontWeight: 600 }}>{rev.rating || "N/A"}</span>
                            </Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>
                              {rev.review_date || "N/A"}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: BRAND.navy, fontStyle: rev.comments ? "normal" : "italic", fontSize: "0.8125rem" }}>
                            {rev.comments || "No comments provided."}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: "center", py: 4, bgcolor: "#fafafa", borderRadius: "12px", border: `1px dashed ${BRAND.border}` }}>
                  <Typography variant="body2" sx={{ color: BRAND.muted }}>
                    No reviewers have been assigned or submitted details yet.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ) : null}
    </FormDrawer>
  );
};

export default AppraisalViewModal;
